/**
 * Validation Functions for Bulk Events
 * 
 * Provides validation logic for bulk event configuration inputs.
 */

export interface ValidationResult {
  isValid: boolean;
  errors: Record<string, string>;
}

export interface DayConfig {
  dayOfWeek: number; // 0=Sunday, 1=Monday, ..., 6=Saturday
  enabled: boolean;
  eventName: string;
  eventType: string;
  locationId: string;
  startTime: string; // HH:mm format
  endTime: string; // HH:mm format
}

export interface BulkEventsConfig {
  startDate: string; // YYYY-MM-DD format
  endDate: string; // YYYY-MM-DD format
  recurrenceWeeks: number; // 1, 2, 3, or 4
  weeklySchedule: DayConfig[];
}

/**
 * Validate date range configuration
 * @param startDate - Start date in YYYY-MM-DD format
 * @param endDate - End date in YYYY-MM-DD format
 * @returns Validation result with any errors
 */
export function validateDateRange(startDate: string, endDate: string): ValidationResult {
  const errors: Record<string, string> = {};

  // Check if dates are provided
  if (!startDate) {
    errors.startDate = 'Start date is required';
  }

  if (!endDate) {
    errors.endDate = 'End date is required';
  }

  // If both dates are provided, validate them
  if (startDate && endDate) {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day

    // Check if dates are valid
    if (isNaN(start.getTime())) {
      errors.startDate = 'Invalid start date';
    }

    if (isNaN(end.getTime())) {
      errors.endDate = 'Invalid end date';
    }

    // Check if start date is not in the past
    if (!isNaN(start.getTime()) && start < today) {
      errors.startDate = 'Start date cannot be in the past';
    }

    // Check if end date is after start date
    if (!isNaN(start.getTime()) && !isNaN(end.getTime()) && end <= start) {
      errors.endDate = 'End date must be after start date';
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
}

/**
 * Validate a single day configuration
 * @param config - Day configuration to validate
 * @param dayName - Name of the day (for error messages)
 * @returns Validation result with any errors
 */
export function validateDayConfig(config: DayConfig, dayName: string): ValidationResult {
  const errors: Record<string, string> = {};

  // Only validate if the day is enabled
  if (!config.enabled) {
    return { isValid: true, errors: {} };
  }

  // Validate event name
  if (!config.eventName || !config.eventName.trim()) {
    errors[`${dayName}_eventName`] = `${dayName}: Event name is required`;
  }

  // Validate event type
  if (!config.eventType || !config.eventType.trim()) {
    errors[`${dayName}_eventType`] = `${dayName}: Event type is required`;
  }

  // Validate location
  if (!config.locationId || !config.locationId.trim()) {
    errors[`${dayName}_locationId`] = `${dayName}: Location is required`;
  }

  // Validate start time
  if (!config.startTime || !config.startTime.trim()) {
    errors[`${dayName}_startTime`] = `${dayName}: Start time is required`;
  } else if (!isValidTimeFormat(config.startTime)) {
    errors[`${dayName}_startTime`] = `${dayName}: Invalid start time format (use HH:mm)`;
  }

  // Validate end time
  if (!config.endTime || !config.endTime.trim()) {
    errors[`${dayName}_endTime`] = `${dayName}: End time is required`;
  } else if (!isValidTimeFormat(config.endTime)) {
    errors[`${dayName}_endTime`] = `${dayName}: Invalid end time format (use HH:mm)`;
  }

  // Validate end time is after start time
  if (
    config.startTime &&
    config.endTime &&
    isValidTimeFormat(config.startTime) &&
    isValidTimeFormat(config.endTime)
  ) {
    const [startHour, startMin] = config.startTime.split(':').map(Number);
    const [endHour, endMin] = config.endTime.split(':').map(Number);

    const startMinutes = startHour * 60 + startMin;
    const endMinutes = endHour * 60 + endMin;

    if (endMinutes <= startMinutes) {
      errors[`${dayName}_endTime`] = `${dayName}: End time must be after start time`;
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
}

/**
 * Validate complete bulk events configuration
 * @param config - Complete bulk events configuration
 * @returns Validation result with any errors
 */
export function validateBulkEventsConfig(config: BulkEventsConfig): ValidationResult {
  const errors: Record<string, string> = {};

  // Validate date range
  const dateRangeResult = validateDateRange(config.startDate, config.endDate);
  Object.assign(errors, dateRangeResult.errors);

  // Validate recurrence pattern
  if (!config.recurrenceWeeks || config.recurrenceWeeks < 1 || config.recurrenceWeeks > 4) {
    errors.recurrenceWeeks = 'Recurrence pattern must be selected (1-4 weeks)';
  }

  // Check if at least one day is enabled
  const enabledDays = config.weeklySchedule.filter((day) => day.enabled);
  if (enabledDays.length === 0) {
    errors.weeklySchedule = 'At least one day must be selected';
  }

  // Validate each enabled day
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  config.weeklySchedule.forEach((dayConfig) => {
    if (dayConfig.enabled) {
      const dayName = dayNames[dayConfig.dayOfWeek];
      const dayResult = validateDayConfig(dayConfig, dayName);
      Object.assign(errors, dayResult.errors);
    }
  });

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
}

/**
 * Check if a time string is in valid HH:mm format
 * @param time - Time string to validate
 * @returns True if valid, false otherwise
 */
function isValidTimeFormat(time: string): boolean {
  const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
  return timeRegex.test(time);
}
